import { z } from 'zod';

// Philippine ID Types
export type PhilippineIdType =
  | 'philid'           // PhilSys National ID
  | 'umid'             // Unified Multi-Purpose ID (SSS/GSIS)
  | 'drivers_license'  // LTO Driver's License
  | 'passport'         // DFA Passport
  | 'voters_id'        // COMELEC Voter's ID
  | 'philhealth'       // PhilHealth ID
  | 'tin'              // BIR Tax Identification Number
  | 'sss'              // SSS ID
  | 'gsis'             // GSIS ID
  | 'prc'              // PRC Professional License
  | 'postal_id';       // Philippine Postal ID

export interface PhilippineIdConfig {
  type: PhilippineIdType;
  name: string;
  nameFil: string;  // Filipino translation
  agency: string;
  pattern: RegExp;
  format: string;
  priority: 'high' | 'medium' | 'low';
  hasQr: boolean;
}

// Philippine ID Configuration
export const PHILIPPINE_IDS: Record<PhilippineIdType, PhilippineIdConfig> = {
  philid: {
    type: 'philid',
    name: 'Philippine National ID (PhilID)',
    nameFil: 'Pambansang ID ng Pilipinas (PhilID)',
    agency: 'Philippine Statistics Authority (PSA)',
    pattern: /^[0-9]{4}-[0-9]{4}-[0-9]{4}-[0-9]{4}$/,  // PSN format: XXXX-XXXX-XXXX-XXXX
    format: 'XXXX-XXXX-XXXX-XXXX (16 digits)',
    priority: 'high',
    hasQr: true
  },
  umid: {
    type: 'umid',
    name: 'Unified Multi-Purpose ID (UMID)',
    nameFil: 'Unified Multi-Purpose ID (UMID)',
    agency: 'SSS/GSIS',
    pattern: /^[0-9]{12}$/,  // 12-digit CRN
    format: 'XXXXXXXXXXXX (12 digits)',
    priority: 'high',
    hasQr: false
  },
  drivers_license: {
    type: 'drivers_license',
    name: "Driver's License",
    nameFil: 'Lisensya sa Pagmamaneho',
    agency: 'Land Transportation Office (LTO)',
    pattern: /^[A-Z][0-9]{2}-[0-9]{2}-[0-9]{6}$/,  // Format: X00-00-000000
    format: 'X00-00-000000',
    priority: 'high',
    hasQr: true
  },
  passport: {
    type: 'passport',
    name: 'Philippine Passport',
    nameFil: 'Pasaporte ng Pilipinas',
    agency: 'Department of Foreign Affairs (DFA)',
    pattern: /^[A-Z]{1,2}[0-9]{7,8}$/,  // Format: P1234567 or EC1234567
    format: 'P1234567 or EC1234567',
    priority: 'high',
    hasQr: false
  },
  voters_id: {
    type: 'voters_id',
    name: "Voter's ID",
    nameFil: 'ID ng Botante',
    agency: 'Commission on Elections (COMELEC)',
    pattern: /^[0-9]{4}-[0-9]{4}[A-Z]-[0-9]{5}[A-Z]{3}[0-9]{2}$/,
    format: 'XXXX-XXXXA-XXXXXAAA00',
    priority: 'medium',
    hasQr: false
  },
  philhealth: {
    type: 'philhealth',
    name: 'PhilHealth ID',
    nameFil: 'PhilHealth ID',
    agency: 'Philippine Health Insurance Corporation',
    pattern: /^[0-9]{2}-[0-9]{9}-[0-9]$/,  // Format: XX-XXXXXXXXX-X
    format: 'XX-XXXXXXXXX-X (12 digits)',
    priority: 'medium',
    hasQr: false
  },
  tin: {
    type: 'tin',
    name: 'Tax Identification Number (TIN)',
    nameFil: 'Tax Identification Number (TIN)',
    agency: 'Bureau of Internal Revenue (BIR)',
    pattern: /^[0-9]{3}-[0-9]{3}-[0-9]{3}(-[0-9]{3,5})?$/,  // Format: XXX-XXX-XXX or XXX-XXX-XXX-XXXXX
    format: 'XXX-XXX-XXX or XXX-XXX-XXX-XXXXX',
    priority: 'medium',
    hasQr: false
  },
  sss: {
    type: 'sss',
    name: 'SSS ID',
    nameFil: 'SSS ID',
    agency: 'Social Security System (SSS)',
    pattern: /^[0-9]{2}-[0-9]{7}-[0-9]$/,  // Format: XX-XXXXXXX-X
    format: 'XX-XXXXXXX-X (10 digits)',
    priority: 'medium',
    hasQr: false
  },
  gsis: {
    type: 'gsis',
    name: 'GSIS ID',
    nameFil: 'GSIS ID',
    agency: 'Government Service Insurance System (GSIS)',
    pattern: /^[0-9]{11}$/,  // 11-digit BP number
    format: 'XXXXXXXXXXX (11 digits)',
    priority: 'medium',
    hasQr: false
  },
  prc: {
    type: 'prc',
    name: 'PRC Professional License',
    nameFil: 'Lisensya ng PRC',
    agency: 'Professional Regulation Commission (PRC)',
    pattern: /^[0-9]{7}$/,  // 7-digit license number
    format: 'XXXXXXX (7 digits)',
    priority: 'low',
    hasQr: false
  },
  postal_id: {
    type: 'postal_id',
    name: 'Postal ID',
    nameFil: 'Postal ID',
    agency: 'Philippine Postal Corporation (PHLPost)',
    pattern: /^[A-Z]{3}[0-9]{10}$/,  // Format: XXX0000000000
    format: 'XXX0000000000',
    priority: 'low',
    hasQr: true
  }
};

// Validation schemas for Philippine IDs
export const philIdSchema = z.object({
  psn: z.string().regex(/^[0-9]{4}-[0-9]{4}-[0-9]{4}-[0-9]{4}$/, 'Invalid PSN format'),
  fullName: z.string().min(1),
  dateOfBirth: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/),
  address: z.string().min(1),
  bloodType: z.string().optional(),
});

export const umidSchema = z.object({
  crn: z.string().regex(/^[0-9]{12}$/, 'Invalid CRN format'),
  fullName: z.string().min(1),
  dateOfBirth: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/),
  sex: z.enum(['M', 'F']),
});

export const ltoLicenseSchema = z.object({
  licenseNumber: z.string().regex(/^[A-Z][0-9]{2}-[0-9]{2}-[0-9]{6}$/, 'Invalid LTO license format'),
  fullName: z.string().min(1),
  dateOfBirth: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/),
  expiryDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/),
  restrictions: z.string().optional(),
  dlCodes: z.string().optional(),
});

export const philippinePassportSchema = z.object({
  passportNumber: z.string().regex(/^[A-Z]{1,2}[0-9]{7,8}$/, 'Invalid passport number'),
  surname: z.string().min(1),
  givenNames: z.string().min(1),
  dateOfBirth: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/),
  expiryDate: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/),
  placeOfBirth: z.string().min(1),
});

export const tinSchema = z.object({
  tin: z.string().regex(/^[0-9]{3}-[0-9]{3}-[0-9]{3}(-[0-9]{3,5})?$/, 'Invalid TIN format'),
  fullName: z.string().min(1),
  rdoCode: z.string().optional(),
});

export const sssSchema = z.object({
  sssNumber: z.string().regex(/^[0-9]{2}-[0-9]{7}-[0-9]$/, 'Invalid SSS number format'),
  fullName: z.string().min(1),
  dateOfBirth: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/),
});

export const philhealthSchema = z.object({
  pin: z.string().regex(/^[0-9]{2}-[0-9]{9}-[0-9]$/, 'Invalid PhilHealth PIN format'),
  fullName: z.string().min(1),
  dateOfBirth: z.string().regex(/^\d{2}\/\d{2}\/\d{4}$/),
  memberType: z.string().optional(),
});

// Validate Philippine ID number format
export function validatePhilippineIdFormat(idNumber: string, idType: PhilippineIdType): boolean {
  const config = PHILIPPINE_IDS[idType];
  if (!config) return false;

  // Remove spaces and dashes for flexible input
  const normalizedId = idNumber.replace(/[\s]/g, '').toUpperCase();

  // For IDs that require dashes, add them if missing
  let formattedId = normalizedId;
  if (idType === 'philid' && !normalizedId.includes('-') && normalizedId.length === 16) {
    formattedId = `${normalizedId.slice(0, 4)}-${normalizedId.slice(4, 8)}-${normalizedId.slice(8, 12)}-${normalizedId.slice(12, 16)}`;
  }
  if (idType === 'tin' && !normalizedId.includes('-') && normalizedId.length >= 9) {
    formattedId = `${normalizedId.slice(0, 3)}-${normalizedId.slice(3, 6)}-${normalizedId.slice(6, 9)}${normalizedId.length > 9 ? '-' + normalizedId.slice(9) : ''}`;
  }
  if (idType === 'sss' && !normalizedId.includes('-') && normalizedId.length === 10) {
    formattedId = `${normalizedId.slice(0, 2)}-${normalizedId.slice(2, 9)}-${normalizedId.slice(9)}`;
  }
  if (idType === 'philhealth' && !normalizedId.includes('-') && normalizedId.length === 12) {
    formattedId = `${normalizedId.slice(0, 2)}-${normalizedId.slice(2, 11)}-${normalizedId.slice(11)}`;
  }

  return config.pattern.test(formattedId);
}

// Format Philippine ID number to standard format
export function formatPhilippineId(idNumber: string, idType: PhilippineIdType): string {
  const digits = idNumber.replace(/[^0-9A-Z]/gi, '').toUpperCase();

  switch (idType) {
    case 'philid':
      if (digits.length === 16) {
        return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8, 12)}-${digits.slice(12, 16)}`;
      }
      break;
    case 'tin':
      if (digits.length >= 9) {
        const base = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}`;
        return digits.length > 9 ? `${base}-${digits.slice(9)}` : base;
      }
      break;
    case 'sss':
      if (digits.length === 10) {
        return `${digits.slice(0, 2)}-${digits.slice(2, 9)}-${digits.slice(9)}`;
      }
      break;
    case 'philhealth':
      if (digits.length === 12) {
        return `${digits.slice(0, 2)}-${digits.slice(2, 11)}-${digits.slice(11)}`;
      }
      break;
    case 'drivers_license':
      if (digits.length === 11) {
        return `${digits.slice(0, 1)}-${digits.slice(1, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
      }
      break;
  }

  return idNumber;
}

// Get list of supported Philippine IDs by priority
export function getPhilippineIdsByPriority(priority?: 'high' | 'medium' | 'low'): PhilippineIdConfig[] {
  const ids = Object.values(PHILIPPINE_IDS);
  if (priority) {
    return ids.filter(id => id.priority === priority);
  }
  return ids.sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
}

// OCR patterns for extracting data from Philippine IDs
export const PH_OCR_PATTERNS = {
  philid: {
    psn: /PSN[:\s]*([0-9]{4}[-\s]?[0-9]{4}[-\s]?[0-9]{4}[-\s]?[0-9]{4})/i,
    fullName: /(?:FULL\s*NAME|PANGALAN)[:\s]*([A-Z\s,]+)/i,
    dateOfBirth: /(?:DATE\s*OF\s*BIRTH|PETSA\s*NG\s*KAPANGANAKAN)[:\s]*(\d{2}[-/]\d{2}[-/]\d{4})/i,
    address: /(?:ADDRESS|TIRAHAN)[:\s]*([A-Z0-9\s,.-]+)/i,
    bloodType: /(?:BLOOD\s*TYPE|URI\s*NG\s*DUGO)[:\s]*([ABO]+[+-]?)/i,
  },
  drivers_license: {
    licenseNumber: /(?:LICENSE\s*NO|LIC\s*NO)[.:\s]*([A-Z][0-9]{2}[-\s]?[0-9]{2}[-\s]?[0-9]{6})/i,
    fullName: /(?:NAME|PANGALAN)[:\s]*([A-Z\s,]+)/i,
    dateOfBirth: /(?:DATE\s*OF\s*BIRTH|DOB)[:\s]*(\d{2}[-/]\d{2}[-/]\d{4})/i,
    expiryDate: /(?:EXPIRY|EXP|VALID\s*UNTIL)[:\s]*(\d{2}[-/]\d{2}[-/]\d{4})/i,
    restrictions: /(?:RESTRICTIONS|RES)[:\s]*([0-9,\s]+)/i,
    dlCodes: /(?:DL\s*CODES?)[:\s]*([A-Z0-9,\s]+)/i,
  },
  sss: {
    sssNumber: /(?:SSS\s*NO|SS\s*NO)[.:\s]*([0-9]{2}[-\s]?[0-9]{7}[-\s]?[0-9])/i,
    fullName: /(?:NAME|PANGALAN)[:\s]*([A-Z\s,]+)/i,
    dateOfBirth: /(?:DATE\s*OF\s*BIRTH|DOB)[:\s]*(\d{2}[-/]\d{2}[-/]\d{4})/i,
  },
  tin: {
    tin: /(?:TIN)[:\s]*([0-9]{3}[-\s]?[0-9]{3}[-\s]?[0-9]{3}(?:[-\s]?[0-9]{3,5})?)/i,
    fullName: /(?:NAME|TAXPAYER)[:\s]*([A-Z\s,]+)/i,
  },
  philhealth: {
    pin: /(?:PIN|PHILHEALTH\s*NO)[.:\s]*([0-9]{2}[-\s]?[0-9]{9}[-\s]?[0-9])/i,
    fullName: /(?:NAME|MEMBER\s*NAME)[:\s]*([A-Z\s,]+)/i,
    dateOfBirth: /(?:DATE\s*OF\s*BIRTH|DOB)[:\s]*(\d{2}[-/]\d{2}[-/]\d{4})/i,
  },
  umid: {
    crn: /(?:CRN)[:\s]*([0-9]{12})/i,
    fullName: /(?:NAME|PANGALAN)[:\s]*([A-Z\s,]+)/i,
    dateOfBirth: /(?:DATE\s*OF\s*BIRTH|DOB)[:\s]*(\d{2}[-/]\d{2}[-/]\d{4})/i,
  },
  passport: {
    passportNumber: /(?:PASSPORT\s*NO)[.:\s]*([A-Z]{1,2}[0-9]{7,8})/i,
    surname: /(?:SURNAME|APELYIDO)[:\s]*([A-Z]+)/i,
    givenNames: /(?:GIVEN\s*NAMES?|MGA\s*PANGALAN)[:\s]*([A-Z\s]+)/i,
    dateOfBirth: /(?:DATE\s*OF\s*BIRTH|PETSA\s*NG\s*KAPANGANAKAN)[:\s]*(\d{2}[-/]\d{2}[-/]\d{4})/i,
    expiryDate: /(?:DATE\s*OF\s*EXPIRY|EXPIRY)[:\s]*(\d{2}[-/]\d{2}[-/]\d{4})/i,
    placeOfBirth: /(?:PLACE\s*OF\s*BIRTH|LUGAR\s*NG\s*KAPANGANAKAN)[:\s]*([A-Z\s,]+)/i,
  },
};

// Extract data from OCR text based on ID type
export function extractPhilippineIdData(text: string, idType: PhilippineIdType): Record<string, string> {
  const patterns = PH_OCR_PATTERNS[idType as keyof typeof PH_OCR_PATTERNS];
  if (!patterns) {
    return {};
  }

  const normalizedText = text.replace(/\s+/g, ' ').toUpperCase();
  const result: Record<string, string> = {};

  for (const [field, pattern] of Object.entries(patterns)) {
    const match = normalizedText.match(pattern);
    if (match && match[1]) {
      result[field] = match[1].trim();
    }
  }

  return result;
}

// Validate extracted Philippine ID data
export function validatePhilippineIdData(data: Record<string, string>, idType: PhilippineIdType): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  switch (idType) {
    case 'philid':
      try {
        philIdSchema.parse({
          psn: data.psn || '',
          fullName: data.fullName || '',
          dateOfBirth: data.dateOfBirth || '',
          address: data.address || '',
          bloodType: data.bloodType,
        });
      } catch (e) {
        if (e instanceof z.ZodError) {
          errors.push(...e.errors.map(err => `${err.path.join('.')}: ${err.message}`));
        }
      }
      break;
    case 'drivers_license':
      try {
        ltoLicenseSchema.parse({
          licenseNumber: data.licenseNumber || '',
          fullName: data.fullName || '',
          dateOfBirth: data.dateOfBirth || '',
          expiryDate: data.expiryDate || '',
          restrictions: data.restrictions,
          dlCodes: data.dlCodes,
        });
      } catch (e) {
        if (e instanceof z.ZodError) {
          errors.push(...e.errors.map(err => `${err.path.join('.')}: ${err.message}`));
        }
      }
      break;
    case 'sss':
      try {
        sssSchema.parse({
          sssNumber: data.sssNumber || '',
          fullName: data.fullName || '',
          dateOfBirth: data.dateOfBirth || '',
        });
      } catch (e) {
        if (e instanceof z.ZodError) {
          errors.push(...e.errors.map(err => `${err.path.join('.')}: ${err.message}`));
        }
      }
      break;
    case 'tin':
      try {
        tinSchema.parse({
          tin: data.tin || '',
          fullName: data.fullName || '',
          rdoCode: data.rdoCode,
        });
      } catch (e) {
        if (e instanceof z.ZodError) {
          errors.push(...e.errors.map(err => `${err.path.join('.')}: ${err.message}`));
        }
      }
      break;
    case 'passport':
      try {
        philippinePassportSchema.parse({
          passportNumber: data.passportNumber || '',
          surname: data.surname || '',
          givenNames: data.givenNames || '',
          dateOfBirth: data.dateOfBirth || '',
          expiryDate: data.expiryDate || '',
          placeOfBirth: data.placeOfBirth || '',
        });
      } catch (e) {
        if (e instanceof z.ZodError) {
          errors.push(...e.errors.map(err => `${err.path.join('.')}: ${err.message}`));
        }
      }
      break;
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// Check if ID is expired (for IDs with expiry dates)
export function isPhilippineIdExpired(expiryDate: string): boolean {
  const [day, month, year] = expiryDate.split(/[-/]/).map(Number);
  const expiry = new Date(year, month - 1, day);
  return expiry < new Date();
}

// ========================================
// PHILIPPINE ID CHECKSUM VALIDATION
// ========================================

/**
 * Calculate Luhn checksum (used for credit cards and some IDs)
 */
export function validateLuhnChecksum(idNumber: string): boolean {
  const digits = idNumber.replace(/\D/g, '');
  let sum = 0;
  let isEven = false;

  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
}

/**
 * SSS Number Validation
 * Format: XX-XXXXXXX-X (10 digits)
 * The last digit is a check digit calculated using modulo 10 with weights
 *
 * Algorithm: Multiply each digit by weights [1,4,3,5,2,6,1,4,3], sum, modulo 10
 * The check digit makes (sum + checkDigit) % 10 = 0
 */
export function validateSSSNumber(sssNumber: string): {
  isValid: boolean;
  error?: string;
  formatted?: string;
} {
  // Remove non-digits
  const digits = sssNumber.replace(/\D/g, '');

  if (digits.length !== 10) {
    return {
      isValid: false,
      error: 'SSS number must be exactly 10 digits',
    };
  }

  // SSS check digit weights
  const weights = [1, 4, 3, 5, 2, 6, 1, 4, 3];

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i], 10) * weights[i];
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  const providedCheckDigit = parseInt(digits[9], 10);

  if (checkDigit !== providedCheckDigit) {
    return {
      isValid: false,
      error: `Invalid SSS check digit. Expected ${checkDigit}, got ${providedCheckDigit}`,
    };
  }

  return {
    isValid: true,
    formatted: `${digits.slice(0, 2)}-${digits.slice(2, 9)}-${digits.slice(9)}`,
  };
}

/**
 * TIN (Tax Identification Number) Validation
 * Format: XXX-XXX-XXX or XXX-XXX-XXX-XXXXX (9 or 12-14 digits)
 *
 * The 9th digit is a check digit calculated using modulo 10 with weights
 * Algorithm: Multiply first 8 digits by weights [8,7,6,5,4,3,2,1], sum mod 10
 */
export function validateTIN(tin: string): {
  isValid: boolean;
  error?: string;
  formatted?: string;
  tinType?: 'individual' | 'business';
} {
  // Remove non-digits
  const digits = tin.replace(/\D/g, '');

  if (digits.length < 9) {
    return {
      isValid: false,
      error: 'TIN must be at least 9 digits',
    };
  }

  if (digits.length > 14) {
    return {
      isValid: false,
      error: 'TIN cannot exceed 14 digits',
    };
  }

  // TIN check digit weights (for first 8 digits)
  const weights = [8, 7, 6, 5, 4, 3, 2, 1];

  let sum = 0;
  for (let i = 0; i < 8; i++) {
    sum += parseInt(digits[i], 10) * weights[i];
  }

  const checkDigit = sum % 10;
  const providedCheckDigit = parseInt(digits[8], 10);

  if (checkDigit !== providedCheckDigit) {
    return {
      isValid: false,
      error: `Invalid TIN check digit. Expected ${checkDigit}, got ${providedCheckDigit}`,
    };
  }

  // Determine TIN type based on branch code
  const tinType = digits.length > 9 ? 'business' : 'individual';

  // Format the TIN
  let formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}`;
  if (digits.length > 9) {
    formatted += `-${digits.slice(9)}`;
  }

  return {
    isValid: true,
    formatted,
    tinType,
  };
}

/**
 * PhilHealth ID Number (PIN) Validation
 * Format: XX-XXXXXXXXX-X (12 digits)
 *
 * The last digit is a check digit using modulo 10 algorithm
 */
export function validatePhilHealthPIN(pin: string): {
  isValid: boolean;
  error?: string;
  formatted?: string;
  memberCategory?: string;
} {
  // Remove non-digits
  const digits = pin.replace(/\D/g, '');

  if (digits.length !== 12) {
    return {
      isValid: false,
      error: 'PhilHealth PIN must be exactly 12 digits',
    };
  }

  // PhilHealth uses alternating weights 2,1
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    let digit = parseInt(digits[i], 10);
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  const providedCheckDigit = parseInt(digits[11], 10);

  if (checkDigit !== providedCheckDigit) {
    return {
      isValid: false,
      error: `Invalid PhilHealth check digit. Expected ${checkDigit}, got ${providedCheckDigit}`,
    };
  }

  // First 2 digits indicate member category
  const categoryCode = digits.slice(0, 2);
  const categories: Record<string, string> = {
    '01': 'Private Employee',
    '02': 'Government Employee',
    '03': 'Individually Paying',
    '04': 'Indigent/Sponsored',
    '05': 'Overseas Filipino Worker',
    '06': 'Lifetime Member',
    '07': 'Senior Citizen',
  };

  return {
    isValid: true,
    formatted: `${digits.slice(0, 2)}-${digits.slice(2, 11)}-${digits.slice(11)}`,
    memberCategory: categories[categoryCode] || 'Unknown',
  };
}

/**
 * PhilID (Philippine National ID) PSN Validation
 * Format: XXXX-XXXX-XXXX-XXXX (16 digits)
 *
 * Uses Luhn algorithm for the check digit
 */
export function validatePhilIDPSN(psn: string): {
  isValid: boolean;
  error?: string;
  formatted?: string;
} {
  // Remove non-digits
  const digits = psn.replace(/\D/g, '');

  if (digits.length !== 16) {
    return {
      isValid: false,
      error: 'PhilID PSN must be exactly 16 digits',
    };
  }

  // PhilID uses Luhn algorithm
  if (!validateLuhnChecksum(digits)) {
    return {
      isValid: false,
      error: 'Invalid PhilID PSN checksum',
    };
  }

  return {
    isValid: true,
    formatted: `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8, 12)}-${digits.slice(12, 16)}`,
  };
}

/**
 * UMID (Unified Multi-Purpose ID) CRN Validation
 * Format: XXXXXXXXXXXX (12 digits)
 *
 * Uses modulo 10 with specific weights
 */
export function validateUMIDCRN(crn: string): {
  isValid: boolean;
  error?: string;
  formatted?: string;
} {
  // Remove non-digits
  const digits = crn.replace(/\D/g, '');

  if (digits.length !== 12) {
    return {
      isValid: false,
      error: 'UMID CRN must be exactly 12 digits',
    };
  }

  // UMID uses weights similar to SSS (first 11 digits, 12th is check)
  const weights = [1, 4, 3, 5, 2, 6, 1, 4, 3, 5, 2];

  let sum = 0;
  for (let i = 0; i < 11; i++) {
    sum += parseInt(digits[i], 10) * weights[i];
  }

  const checkDigit = (10 - (sum % 10)) % 10;
  const providedCheckDigit = parseInt(digits[11], 10);

  if (checkDigit !== providedCheckDigit) {
    return {
      isValid: false,
      error: `Invalid UMID check digit. Expected ${checkDigit}, got ${providedCheckDigit}`,
    };
  }

  return {
    isValid: true,
    formatted: digits, // UMID doesn't have separators
  };
}

/**
 * LTO Driver's License Validation
 * Format: X00-00-000000 (letter + 10 digits)
 *
 * First letter indicates region, check digit is last digit
 */
export function validateLTOLicense(license: string): {
  isValid: boolean;
  error?: string;
  formatted?: string;
  region?: string;
} {
  // Normalize: remove spaces and dashes
  const normalized = license.replace(/[\s-]/g, '').toUpperCase();

  if (normalized.length !== 11) {
    return {
      isValid: false,
      error: 'LTO license must be 11 characters (1 letter + 10 digits)',
    };
  }

  const letter = normalized[0];
  const digits = normalized.slice(1);

  if (!/^[A-Z]$/.test(letter)) {
    return {
      isValid: false,
      error: 'LTO license must start with a letter',
    };
  }

  if (!/^\d{10}$/.test(digits)) {
    return {
      isValid: false,
      error: 'LTO license must have 10 digits after the letter',
    };
  }

  // Region codes (first letter)
  const regions: Record<string, string> = {
    'A': 'NCR - National Capital Region',
    'B': 'CAR - Cordillera Administrative Region',
    'C': 'Region I - Ilocos Region',
    'D': 'Region II - Cagayan Valley',
    'E': 'Region III - Central Luzon',
    'F': 'Region IV-A - CALABARZON',
    'G': 'Region IV-B - MIMAROPA',
    'H': 'Region V - Bicol Region',
    'I': 'Region VI - Western Visayas',
    'J': 'Region VII - Central Visayas',
    'K': 'Region VIII - Eastern Visayas',
    'L': 'Region IX - Zamboanga Peninsula',
    'M': 'Region X - Northern Mindanao',
    'N': 'Region XI - Davao Region',
    'O': 'Region XII - SOCCSKSARGEN',
    'P': 'Region XIII - Caraga',
    'Q': 'BARMM - Bangsamoro',
  };

  return {
    isValid: true,
    formatted: `${letter}${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`,
    region: regions[letter] || 'Unknown Region',
  };
}

/**
 * Comprehensive Philippine ID validation
 * Validates format AND checksum where applicable
 */
export function validatePhilippineIdComplete(idNumber: string, idType: PhilippineIdType): {
  isValid: boolean;
  errors: string[];
  formatted?: string;
  details?: Record<string, any>;
} {
  const errors: string[] = [];
  let result: any = {};

  // First validate format
  if (!validatePhilippineIdFormat(idNumber, idType)) {
    errors.push(`Invalid ${idType.toUpperCase()} format`);
  }

  // Then validate checksum based on ID type
  switch (idType) {
    case 'sss':
      result = validateSSSNumber(idNumber);
      if (!result.isValid && result.error) {
        errors.push(result.error);
      }
      break;

    case 'tin':
      result = validateTIN(idNumber);
      if (!result.isValid && result.error) {
        errors.push(result.error);
      }
      break;

    case 'philhealth':
      result = validatePhilHealthPIN(idNumber);
      if (!result.isValid && result.error) {
        errors.push(result.error);
      }
      break;

    case 'philid':
      result = validatePhilIDPSN(idNumber);
      if (!result.isValid && result.error) {
        errors.push(result.error);
      }
      break;

    case 'umid':
      result = validateUMIDCRN(idNumber);
      if (!result.isValid && result.error) {
        errors.push(result.error);
      }
      break;

    case 'drivers_license':
      result = validateLTOLicense(idNumber);
      if (!result.isValid && result.error) {
        errors.push(result.error);
      }
      break;

    default:
      // For other IDs, only format validation is available
      result = { isValid: errors.length === 0 };
  }

  return {
    isValid: errors.length === 0,
    errors,
    formatted: result.formatted,
    details: {
      ...result,
      idType,
      idConfig: PHILIPPINE_IDS[idType],
    },
  };
}

// ========================================
// PHILSYS QR AUTHENTICITY LOGIC
// ========================================

/**
 * Parses the base64-encoded signed string from a PhilID QR code.
 * Even without the official API, we can extract the plain-text fields
 * to verify they match the OCR data on the front of the ID.
 */
export function parsePhilSysQR(qrText: string): Record<string, string> {
  try {
    // PhilSys V2 often uses a base64 encoded JSON
    // V1 uses a specific pipe-separated or signed format
    if (qrText.startsWith('V2:')) {
      const payload = qrText.substring(3);
      const decoded = atob(payload);
      return JSON.parse(decoded);
    }

    // Fallback/Legacy format parsing
    const fields: Record<string, string> = {};
    if (qrText.includes('|')) {
      const parts = qrText.split('|');
      fields.psn = parts[0];
      fields.lastName = parts[1];
      fields.firstName = parts[2];
      fields.dob = parts[3];
    }
    return fields;
  } catch (e) {
    console.error('Failed to parse PhilSys QR:', e);
    return {};
  }
}

/**
 * Compares OCR results with QR data to detect fraud.
 * A common fraud is changing the name on the ID but leaving the QR code intact.
 */
export function crossCheckQRData(ocr: Record<string, string>, qr: Record<string, string>): {
  isMatch: boolean;
  mismatchedFields: string[];
} {
  const mismatches: string[] = [];

  // Normalize strings for comparison
  const normalize = (s: string) => (s || '').replace(/[^A-Z0-9]/gi, '').toUpperCase();

  // Check PSN (National ID Number)
  if (qr.psn && ocr.psn) {
    if (normalize(qr.psn) !== normalize(ocr.psn)) {
      mismatches.push('PSN/ID_NUMBER');
    }
  }

  // Check Full Name
  if (qr.fullName && ocr.fullName) {
    // Basic fuzzy match - check if major parts of the name match
    const qrName = normalize(qr.fullName);
    const ocrName = normalize(ocr.fullName);
    if (!qrName.includes(ocrName) && !ocrName.includes(qrName)) {
      mismatches.push('FULL_NAME');
    }
  }

  return {
    isMatch: mismatches.length === 0,
    mismatchedFields: mismatches
  };
}
